import React, {createContext, useContext} from 'react';

import type {ColorPaletteOption} from '../../util/colors';
import type {SingleRunNavParams} from '../MultiRunWorkspace/SingleRunMode/buildSingleRunLink';
import type {PanelConfigContextType} from './PanelConfigContext';

/**
 * Settings the chart renderers and crosshair read. Graph also passes some to
 * the legend and legend warning views as props. GraphContainer reads them
 * from app state and GraphWrapper provides them inside the unit, so components
 * below Graph never read app state for them (rule 1: a context provided inside
 * the unit is internal).
 */
export type LinePlotSettings = Pick<
  PanelConfigContextType,
  | 'colorRunNames'
  | 'displayFullRunName'
  | 'entityName'
  | 'forceReactVis'
  | 'groupingDepth'
  | 'highlightedCompanionRunOnly'
  | 'isFullFidelity'
  | 'isMultiMetric'
  | 'limit'
  | 'plotType'
  | 'pointVisualizationMethod'
  | 'projectName'
  | 'tooltipNumberOfRuns'
  | 'xAxis'
  | 'xAxisFormat'
  | 'xAxisIsDatetime'
  | 'xLogScale'
  | 'yLogScale'
> & {
  colorPaletteOption: ColorPaletteOption;
  /**
   * Returns the named workspace search string (`?nw=...`) for run links. A
   * function, so the crosshair reads the URL when it builds a link.
   */
  getNamedWorkspaceSearch: () => string;
  /** Whether the workspace has a media panel, so the crosshair shows the snap hint. */
  hasMediaPanel: boolean;
  isAnyRunsetGrouped: boolean;
  isDarkMode: boolean;
  isSingleRun: boolean;
  /** The user pressed Shift+S on the crosshair: move media panel sliders to this step. */
  onSnapMediaPanelsToStep: (step: number, xAxis: string) => void;
  /** The workspace or section "show x range" setting, resolved. */
  showXRange: boolean;
  /** The current workspace's route, for the crosshair's cmd-click run link. */
  singleRunNavParams: SingleRunNavParams;
  /** Whether the viewer may use uPlot (the `uplot_user` flag). */
  uplotEnabled: boolean;
};

/** Internal renderer settings exclude host navigation and media actions. */
export type GraphRendererSettings = Omit<
  LinePlotSettings,
  | 'colorPaletteOption'
  | 'xAxisFormat'
  | 'groupingDepth'
  | 'isAnyRunsetGrouped'
  | 'limit'
  | 'colorRunNames'
  | 'displayFullRunName'
  | 'entityName'
  | 'getNamedWorkspaceSearch'
  | 'hasMediaPanel'
  | 'highlightedCompanionRunOnly'
  | 'isSingleRun'
  | 'onSnapMediaPanelsToStep'
  | 'projectName'
  | 'showXRange'
  | 'singleRunNavParams'
  | 'tooltipNumberOfRuns'
>;

const LinePlotSettingsContext = createContext<GraphRendererSettings | null>(
  null
);

/**
 * GraphWrapper's provider. The value must keep its identity while its fields
 * are unchanged.
 */
export function LinePlotSettingsProvider({
  children,
  settings,
}: {
  children: React.ReactNode;
  settings: GraphRendererSettings;
}) {
  return (
    <LinePlotSettingsContext.Provider value={settings}>
      {children}
    </LinePlotSettingsContext.Provider>
  );
}

export function useLinePlotSettings(): GraphRendererSettings {
  const settings = useContext(LinePlotSettingsContext);
  if (settings == null) {
    throw new Error(
      'useLinePlotSettings must be called inside Graph, which provides the line plot settings'
    );
  }
  return settings;
}
